import type { IPlayerWrap } from './PlayerWrap.h'

import { LayoutBlockPlayer } from "/app/libraries/arcana/src/blocks/LayoutBlock/Templates/index.ts"

import { Events, Player } from '@setplex/pureya'
import { api } from '@setplex/tria-api'
import { useUnit } from 'effector-react'
import { memo, useEffect, useMemo } from 'react'
import { model as i18n } from '~/core/i18n'
import { model as router } from '~/core/router'
import {
  model as entityPlayerModel,
  playerTrailerModel,
} from '~/entities/player'
import { useEmitPlayerMeta } from '~/features/analytix'
import { featuresMaxAutoQualityAudioModel } from '~/features/max-auto-quality/audio'
import { featuresMaxAutoQualityVideoModel } from '~/features/max-auto-quality/video'
import { useNpaw } from '~/features/npaw'
import { usePlaybackUrlAdParams } from '~/features/playback-url-ad-params'
import { useLastPositionSave } from '~/features/player'
import { usePlaybackUrlWithRestartAttempt } from '~/features/smart-restart'
import { ONE_STEP_REWIND_SEC } from '~/shared/constants/rewind'
import { useChromecast, useCmcd } from '~/widgets/Player'
import { PLAYER_VIEWS } from '~/widgets/Player/hooks/PlayerOrderedViews'
import { GEOBLOCK_URL_ERROR_CODES } from '~/widgets/Player/model/error-map'
import { usePlayerSubscriptionLimitations } from '~/widgets/PlayerSubscriptionLimitations'
import { useLastWatchedContent } from '../hooks/useLastWatchedContent'

// player requires variables `variables`
// but they are the same as in arcana, so here player works without issues
import './playerThemeVars.css'

// export memoized component
export const PlayerWrap = memo(PlayerWrap_)

PlayerWrap_.displayName = 'PlayerWrap'
function PlayerWrap_({
  player,
  using,
  miniPlayer,
  mediaUrlData,
  title,
  subtitle,
  contentId,
  contentType,
  contentKey,
  contentProgram,
  blockedByAcl,
  activeView,
  safeToShow,
  pinLocked,
  poster,
  logo,
  liveProgressBar,
  onPlaybackEnded,
  onCloseClick = router.navigateBackOrHome,
  onInfoClick,
  onPlaylistClick,
  onPlayNextEpisode,
  onPlayNext,
  onPlayPrevious,
  skipSegments: skipSegmentsProp,
  hideSkipSegments,
  onProgressReachedEnd,
  onProgramClick,
  onRedirectTvClick,
  onPlaying,
  onTokenExpired,
  isRewindPending,
  isTrailer,
  muted,
  autoplay,
  npawData,
  className,
  videoClassName,
  infoPopup,
  isRewound,
  onLiveRewind,
  onDelayedLiveRewind,
  onGoLive,
  rewindDetails,
  children,
  dolbyAtmosAvailable,
  isSmartlibEnabled,
  isPlayerDisabled,
  isContentPaid,
  onReady,
  visible,
  autoplayTrailer,
  contentTitle,
}: IPlayerWrap) {
  const {
    playbackUrl: rawSourceUrl = '',
    drm: sourceDrm,
    headers: sourceHeaders,
  } = mediaUrlData ?? {}
  const isUrlRequestPending = useUnit(entityPlayerModel.$isUrlRequestPending)
  const sourceUrl = usePlaybackUrlWithRestartAttempt(
    rawSourceUrl,
    isUrlRequestPending
  )

  const skipSegments = useMemo(() => {
    if (hideSkipSegments) return undefined

    return skipSegmentsProp
  }, [hideSkipSegments, skipSegmentsProp])

  const { subscriptionLimitationsModals, isVideoBlocked } =
    usePlayerSubscriptionLimitations({
      sourceUrl: sourceUrl,
      player,
      isRewound,
      miniPlayer,
    })

  const trailerCategoryName = useUnit(
    playerTrailerModel.$autoTrailerCategoryName
  )

  const playerDisabled =
    !safeToShow || activeView || blockedByAcl || pinLocked || isVideoBlocked

  const sourceUrlChecked = contentKey && !playerDisabled ? sourceUrl : undefined

  // add google pal nonce and other ad params to sourceUrl
  const { url: playbackUrl, drm: playbackDrm } = usePlaybackUrlAdParams(
    { url: sourceUrlChecked, drm: sourceDrm, contentId },
    player.container
  )

  // Chromecast configuration
  const { chromecast, stoppedTime } = useChromecast({
    title,
    subtitle,
    drm: playbackDrm,
    player,
    contentId,
  })

  useLastWatchedContent({ title: contentTitle, autoplayTrailer })

  const locale = useUnit(i18n.$locale)

  const maxAutoQuality = useUnit(
    featuresMaxAutoQualityVideoModel.$currentMaxAutoQuality
  )

  // Commented until we enable smartlib for all players
  // const isSmartlibEnabled = useUnit(model.$isSmartlibEnabled)

  const isDolbyAtmosPreferred = useUnit(
    featuresMaxAutoQualityAudioModel.$currentDolbyAtmosPreferred
  )

  const dolby = useMemo(
    () => ({
      enabled: dolbyAtmosAvailable ?? false,
      preferDolbyAtmos: isDolbyAtmosPreferred,
    }),
    [dolbyAtmosAvailable, isDolbyAtmosPreferred]
  )

  const routeApiError =
    useUnit(api.player.$currentPlayerPageUrlError) ?? undefined

  // Save stoppedTime for later
  useLastPositionSave({
    player,
    contentType,
    isTrailer,
    stoppedTime,
  })

  // Npaw
  useNpaw({
    enable: Boolean(playbackUrl),
    npawData,
    drm: playbackDrm,
    on: player.events.on,
    playerInstance: player.instance,
  })

  // Analytix
  useEmitPlayerMeta(
    {
      id: contentId,
      type: contentType,
      url: playbackUrl,
      name: title,
      program: contentProgram,
      trailer: isTrailer,
      paid: isContentPaid,
      autoplayTrailer,
      trailerCategoryName,
    },
    player.instance
  )

  // CMCD configuration
  const cmcd = useCmcd(contentId)

  const isUrlGeoblockError = GEOBLOCK_URL_ERROR_CODES.has(
    routeApiError?.errorCode ?? ''
  )

  const isPosterShown =
    activeView === PLAYER_VIEWS.geoLock ||
    activeView === PLAYER_VIEWS.payment ||
    activeView === PLAYER_VIEWS.signInToWatch ||
    isUrlGeoblockError

  const isErrorShowAllowed = safeToShow && contentKey
  const externalError = isErrorShowAllowed ? routeApiError : null

  useEffect(() => {
    player.instance?.trigger(Events.Player.TogglePopup, {
      isOpen: infoPopup?.condition,
    })
  }, [infoPopup?.condition, player.instance])

  return (
    <LayoutBlockPlayer
      miniPlayer={miniPlayer}
      visible={visible}
      autoplayTrailer={autoplayTrailer}
    >
      <Player
        ref={player.ref}
        using={using}
        sourceUrl={playbackUrl}
        sourceHeaders={sourceHeaders}
        contentKey={contentKey}
        drm={playbackDrm}
        title={title}
        subtitle={subtitle}
        cmcd={cmcd}
        chromecast={chromecast}
        locale={locale}
        externalError={externalError}
        poster={isPosterShown ? poster : undefined}
        logo={logo}
        liveProgressBar={liveProgressBar}
        onCloseClick={onCloseClick}
        onInfoClick={onInfoClick}
        onPlaylistClick={onPlaylistClick}
        onPlayNextEpisode={onPlayNextEpisode}
        onPlayNext={onPlayNext}
        onPlayPrevious={onPlayPrevious}
        skipSegments={skipSegments}
        onPlaybackEnded={onPlaybackEnded}
        onProgressReachedEnd={onProgressReachedEnd}
        onProgramClick={onProgramClick}
        onRedirectTvClick={onRedirectTvClick}
        onPlaying={onPlaying}
        onTokenExpired={onTokenExpired}
        isUrlRequestPending={isUrlRequestPending}
        isRewindPending={isRewindPending}
        muted={muted}
        seekStep={ONE_STEP_REWIND_SEC}
        autoplay={autoplay}
        className={className}
        videoClassName={videoClassName}
        isRewound={isRewound}
        onLiveRewind={onLiveRewind}
        onDelayedLiveRewind={onDelayedLiveRewind}
        onGoLive={onGoLive}
        rewindDetails={rewindDetails}
        isSmartlibEnabled={isSmartlibEnabled}
        isPlayerDisabled={isPlayerDisabled}
        dolby={dolby}
        maxAutoQuality={maxAutoQuality}
        onReady={onReady}
      >
        <>
          {children}
          {subscriptionLimitationsModals}
        </>
      </Player>
    </LayoutBlockPlayer>
  )
}
